import { FileInfo, ValidationResult, UploadProgress } from '../types';
import { validateFile, getFileExtension } from '../utils/fileValidation';

// Storage key for mock files
const STORAGE_KEY = 'gis_uploaded_files';

// Generate unique file ID
const generateFileId = (): string => {
    return `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// Get file format from extension
const getFileFormat = (filename: string): 'shp' | 'zip' | 'gpkg' => {
    const ext = getFileExtension(filename);
    if (ext === '.gpkg') return 'gpkg';
    if (ext === '.shp') return 'shp';
    return 'zip';
};

/**
 * File Service - handles file operations (mock implementation)
 */
export const fileService = {
    /**
     * Validate a file before upload
     */
    validateFile(file: File): ValidationResult {
        return validateFile(file);
    },

    /**
     * Upload a file (mock - simulates upload with progress)
     */
    async uploadFile(
        file: File,
        onProgress?: (progress: UploadProgress) => void
    ): Promise<FileInfo> {
        const validation = this.validateFile(file);
        if (!validation.valid) {
            throw new Error(validation.errors[0]?.message || 'Validation failed');
        }

        const fileId = generateFileId();
        const totalBytes = file.size;
        let bytesUploaded = 0;

        // Simulate upload progress
        return new Promise((resolve) => {
            const interval = setInterval(() => {
                bytesUploaded += totalBytes / 10;
                if (bytesUploaded >= totalBytes) {
                    bytesUploaded = totalBytes;
                    clearInterval(interval);

                    const fileInfo: FileInfo = {
                        id: fileId,
                        name: file.name,
                        size: file.size,
                        type: getFileFormat(file.name),
                        uploadedAt: new Date(),
                        status: 'uploaded',
                    };

                    // Store in localStorage
                    this.saveFile(fileInfo);

                    resolve(fileInfo);
                }

                if (onProgress) {
                    onProgress({
                        fileId,
                        bytesUploaded: Math.min(bytesUploaded, totalBytes),
                        totalBytes,
                        percentage: Math.min(Math.round((bytesUploaded / totalBytes) * 100), 100),
                    });
                }
            }, 200);
        });
    },

    /**
     * Save file info to localStorage
     */
    saveFile(fileInfo: FileInfo): void {
        const files = this.listFilesSync();
        files.push(fileInfo);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(files));
    },

    /**
     * Get file info by ID
     */
    async getFile(fileId: string): Promise<FileInfo | null> {
        const files = this.listFilesSync();
        return files.find((f) => f.id === fileId) || null;
    },

    /**
     * Delete a file by ID
     */
    async deleteFile(fileId: string): Promise<boolean> {
        const files = this.listFilesSync();
        const filtered = files.filter((f) => f.id !== fileId);
        if (filtered.length !== files.length) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
            return true;
        }
        return false;
    },

    /**
     * List all uploaded files
     */
    async listFiles(): Promise<FileInfo[]> {
        return this.listFilesSync();
    },

    /**
     * Sync version of listFiles
     */
    listFilesSync(): FileInfo[] {
        try {
            const data = localStorage.getItem(STORAGE_KEY);
            if (!data) return [];
            return JSON.parse(data).map((f: FileInfo) => ({
                ...f,
                uploadedAt: new Date(f.uploadedAt),
            }));
        } catch {
            return [];
        }
    },

    /**
     * Clear all files
     */
    clearAll(): void {
        localStorage.removeItem(STORAGE_KEY);
    },
};

export default fileService;
